import { Box, Skeleton, Text, UnstyledButton } from '@mantine/core';
import { IconPhotoOff } from '@tabler/icons-react';
import React from 'react';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { useResolvedImageUrl } from 'src/design/organisms/ImageUpload/useResolvedImageUrl';

export interface IImageThumbnailProps {
  id: string;
  /** Accessible description of the image; falls back to 'Uploaded image'. */
  alt?: string;
  size?: number;
  resolveImageUrl: (id: string) => Promise<string>;
  /** What activating the tile does (download, or open a preview). */
  onClick: () => void;
  /** Omit to render without a remove control. */
  onRemoveClick?: () => void;
}

/** @registry Plain image tile that resolves and displays an uploaded image by id via useResolvedImageUrl, with a real failure state instead of silently disappearing. */
export const ImageThumbnail: React.FC<IImageThumbnailProps> = ({
  id,
  alt,
  size = 96,
  resolveImageUrl,
  onClick,
  onRemoveClick,
}) => {
  const { src, failed } = useResolvedImageUrl(id, resolveImageUrl);

  const description = alt ?? 'Uploaded image';

  const removeControl = onRemoveClick && (
    <DeleteActionIcon
      pos="absolute"
      top={4}
      right={4}
      variant="filled"
      color="dark"
      radius="xl"
      size="sm"
      label="Remove image"
      onDelete={onRemoveClick}
    />
  );

  if (!src && !failed) {
    return <Skeleton w={size} h={size} />;
  }

  if (failed) {
    return (
      <Box
        pos="relative"
        w={size}
        h={size}
        display="flex"
        style={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--mantine-color-gray-4)',
          borderRadius: 'var(--mantine-radius-sm)',
        }}
      >
        <IconPhotoOff size={24} />
        <Text size="xs" c="dimmed">
          Unavailable
        </Text>
        {removeControl}
      </Box>
    );
  }

  return (
    <Box pos="relative" w={size} h={size} display="inline-block">
      <UnstyledButton onClick={onClick} aria-label={description}>
        <img
          src={src ?? undefined}
          width={size}
          height={size}
          alt={description}
          style={{ objectFit: 'cover', display: 'block' }}
        />
      </UnstyledButton>
      {removeControl}
    </Box>
  );
};
